import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ClientViewContext = createContext(null);

export function ClientViewProvider({ children }) {
  const { permissions } = useAuth();
  // Used only by internal users who manually enter a client view
  const [manualClientView, setManualClientView] = useState(null);
  const navigate = useNavigate();

  const isClientMode = !!(permissions?.clientMode && permissions?.clientCompanyId);

  // For real client-mode users, derive clientView directly from permissions (no useEffect delay).
  // For internal users previewing a client, use the manually-set value.
  const clientView = isClientMode
    ? { id: permissions.clientCompanyId, name: permissions.clientCompanyName || '' }
    : manualClientView;

  const enterClientView = useCallback((client) => {
    setManualClientView({ id: client.id, name: client.name });
    navigate(`/portal/${client.id}`);
  }, [navigate]);

  const exitClientView = useCallback(() => {
    if (isClientMode) {
      // Real client users can't exit — send them back to their portal
      navigate(`/portal/${permissions.clientCompanyId}`);
      return;
    }
    setManualClientView(null);
    navigate('/dashboard');
  }, [isClientMode, navigate, permissions]);

  return (
    <ClientViewContext.Provider value={{ clientView, enterClientView, exitClientView, isClientMode }}>
      {children}
    </ClientViewContext.Provider>
  );
}

export function useClientView() {
  const ctx = useContext(ClientViewContext);
  if (!ctx) throw new Error('useClientView must be used within ClientViewProvider');
  return ctx;
}
